import Dropdown, {
  GroupBase,
  Props,
  ActionMeta,
  OnChangeValue,
  components,
  IndicatorsContainerProps,
  CSSObjectWithLabel,
  ControlProps,
} from 'react-select'
import './Select.css'

export type DropdownProps<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Props<Option, IsMulti, Group>

export interface ISelectProps<Option, IsMulti extends boolean = false>
  extends Omit<DropdownProps<Option, IsMulti>, 'onChange'>,
    ISelectCustomProps<Option, IsMulti> {
  formName: string
  fieldName: string
  fieldLabel: string
  testId?: string
  disabled?: boolean
  fieldLabelHidden?: boolean
  errorText?: string
  infoText?: string
}

export interface ISelectCustomProps<Option, IsMulti extends boolean = false> {
  onChange?: (
    newValue: OnChangeValue<Option, IsMulti>,
    actionMeta?: ActionMeta<Option>
  ) => void
}

/** Props for the <strong>react-select</strong> library available here: https://react-select.com/props */
const Select = <Option extends object, IsMulti extends boolean = false>(
  props: ISelectProps<Option, IsMulti>
) => {
  const {
    classNamePrefix,
    formName,
    fieldName,
    fieldLabel,
    testId = 'select',
    disabled = false,
    fieldLabelHidden = false,
    errorText = '',
    infoText = '',
    ...otherProps
  } = props
  const customClassNamePrefix = `${
    classNamePrefix ? `${classNamePrefix}-Select` : 'Select'
  }`

  const getBorderColour = (state: {
    isFocused: boolean
    menuIsOpen: boolean
  }) => {
    if (errorText.length > 0 && !disabled) {
      return '#E00000'
    } else if (errorText.length == 0 && (state.isFocused || state.menuIsOpen)) {
      return '#0274DE'
    } else return '#9E9E9E'
  }

  /** Adds a custom icon for the error state */
  const IndicatorsContainer = (
    props: IndicatorsContainerProps<Option, IsMulti, GroupBase<Option>>
  ) => {
    return (
      <div>
        <components.IndicatorsContainer<Option, IsMulti, GroupBase<Option>>
          {...props}
        >
          {errorText.length > 0 && !disabled && (
            <i
              data-testid="error-icon"
              className="bxf bx-error-circle text-[#E00000] text-lg"
            ></i>
          )}
          {props.children}
        </components.IndicatorsContainer>
      </div>
    )
  }

  const handleChange = (
    newValue: OnChangeValue<Option, IsMulti>,
    actionMeta: ActionMeta<Option>
  ) => {
    if (props.onChange && newValue !== null && newValue !== undefined) {
      props.onChange(newValue, actionMeta)
    }
  }

  return (
    <div className="font-roboto" data-testid={testId}>
      <label
        id={fieldLabel.replace(' ', '-').toLowerCase()}
        htmlFor={`${formName}-${fieldName}`}
        className={`${
          fieldLabelHidden
            ? 'sr-only'
            : `${disabled ? 'input-label--disabled' : 'input-label'}`
        }`}
      >
        {fieldLabel}
      </label>
      <Dropdown
        aria-labelledby={fieldLabel.replace(' ', '-').toLowerCase()}
        classNamePrefix={customClassNamePrefix}
        {...otherProps}
        escapeClearsValue={true}
        id={`${formName}-${fieldName}`}
        isDisabled={disabled}
        components={{ IndicatorsContainer }}
        className="Select"
        styles={{
          control: (
            baseStyles: CSSObjectWithLabel,
            state: ControlProps<Option, IsMulti, GroupBase<Option>>
          ) => ({
            ...baseStyles,
            borderColor: getBorderColour(state),
            '&:hover': {
              borderColor: getBorderColour(state),
            },
            ...(state.isDisabled && {
              pointerEvents: 'auto',
              cursor: 'not-allowed',
              background: '#A6A6A6',
            }),
            boxShadow: 'none',
            paddingTop: '4px',
            paddingBottom: '4px',
            borderRadius: '5px',
          }),
          clearIndicator: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            color: '#515151',
          }),
          dropdownIndicator: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            color: '#515151',
          }),
          placeholder: (baseStyles: CSSObjectWithLabel) => ({
            ...baseStyles,
            color: '#6F767B',
          }),
        }}
        onChange={handleChange}
      />
      {errorText && !disabled && (
        <span className="flex items-center mt-1 input-info-text text-error-7">
          {errorText}
        </span>
      )}
      {infoText && !errorText && !disabled && (
        <span className="block mt-1 input-info-text text-[#808080]">
          {infoText}
        </span>
      )}
    </div>
  )
}

export default Select
